import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar, faUsers, faMusic, faMicrophone } from '@fortawesome/free-solid-svg-icons'
import api from '@/services/api'
import { UserDetailsPanel, UserActionModal } from '@/components/User'
import type { DetailSection, UserAction } from '@/components/User'
import type { Singer } from '@/components/User'

/**
 * Singer Details Page (Using reusable components)
 * Dark mode geek aesthetic - Desktop only
 */
export default function SingerDetailsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [singer, setSinger] = useState<Singer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    action: UserAction | null
    isLoading: boolean
  }>({
    isOpen: false,
    action: null,
    isLoading: false,
  })

  // Fetch singer details
  useEffect(() => {
    if (!id) {
      navigate('/singers')
      return
    }

    const fetchSingerDetails = async () => {
      try {
        const response = await api.get(`/superadmin/users/${id}`)
        if (response.data.success) {
          setSinger(response.data.data)
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchSingerDetails()
  }, [id, navigate, t])

  // Handle action modal open
  const handleAction = (action: UserAction) => {
    setModalState({
      isOpen: true,
      action,
      isLoading: false,
    })
  }

  // Handle action confirm
  const handleConfirmAction = async () => {
    if (!singer || !modalState.action) return

    // Guest singers can only be deleted, not suspended/unsuspended/reset-password
    if (singer.isGuest && modalState.action !== 'delete') {
      setError('Cette action n\'est pas disponible pour les chanteurs invités')
      setModalState({ isOpen: false, action: null, isLoading: false })
      return
    }

    setModalState((prev) => ({ ...prev, isLoading: true }))

    try {
      const singerId = singer.id

      switch (modalState.action) {
        case 'suspend':
          await api.put(`/superadmin/users/${singerId}/status`, { status: 'suspended' })
          setSinger({ ...singer, isSuspended: true })
          break

        case 'unsuspend':
          await api.put(`/superadmin/users/${singerId}/status`, { status: 'active' })
          setSinger({ ...singer, isSuspended: false })
          break

        case 'delete':
          // For guest singers, we can't delete via the user endpoint
          // They would need to be deleted from the Singer collection directly
          // For now, we'll just navigate back to the list
          if (singer.isGuest) {
            // TODO: Implement guest deletion endpoint if needed
            navigate('/singers')
            return
          } else {
            await api.delete(`/superadmin/users/${singerId}`)
            navigate('/singers')
            return
          }

        case 'reset-password':
          await api.post(`/superadmin/users/${singerId}/reset-password`)
          break

        default:
          break
      }

      setModalState({ isOpen: false, action: null, isLoading: false })
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'))
      setModalState({ isOpen: false, action: null, isLoading: false })
    }
  }

  // Define detail sections (adapt for guests)
  const detailSections: DetailSection[] = [
    {
      key: 'identity',
      label: 'Informations d\'identité',
      rows: [
        ...(singer?.isGuest
          ? [
              {
                key: 'guestInfo',
                label: 'Type',
                render: () => (
                  <span className="px-3 py-1 rounded-full text-xs font-bold inline-block bg-purple-900/50 text-purple-300 border border-purple-700/50">
                    Chanteur invité (pas de compte)
                  </span>
                ),
              },
            ]
          : [{ key: 'email', label: t('common.email'), render: (s: Singer) => s.email || '—' }]),
        ...(singer?.isGuest
          ? []
          : [
              { key: 'firstName', label: t('common.firstName'), render: (s: Singer) => s.profile?.firstName || '—' },
              { key: 'lastName', label: t('common.lastName'), render: (s: Singer) => s.profile?.lastName || '—' },
            ]),
        { key: 'pseudo', label: t('common.pseudo'), render: (s: Singer) => s.profile?.pseudo || '—' },
        {
          key: 'createdAt',
          label: singer?.isGuest ? 'Date de première participation' : t('common.joinedDate'),
          render: (s: Singer) => new Date(s.createdAt).toLocaleDateString('fr-FR'),
        },
        ...(singer?.isGuest
          ? []
          : [
              {
                key: 'status',
                label: t('common.status'),
                render: (s: Singer) => (
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold inline-block ${
                      s.isSuspended
                        ? 'bg-red-900/50 text-red-300 border border-red-700/50'
                        : 'bg-green-900/50 text-green-300 border border-green-700/50'
                    }`}
                  >
                    {s.isSuspended ? t('common.suspended') : t('common.active')}
                  </span>
                ),
              },
            ]),
      ],
    },
    {
      key: 'activity',
      label: 'Activité & Statistiques',
      rows: [
        {
          key: 'eventsJoined',
          label: t('superadmin.eventsJoined'),
          render: (s: Singer) => s.stats?.eventCount || s.events?.length || 0,
        },
        {
          key: 'updatedAt',
          label: 'Dernière mise à jour',
          render: (s: Singer) => new Date(s.updatedAt || s.createdAt).toLocaleDateString('fr-FR'),
        },
        ...(singer?.isGuest
          ? []
          : [
              {
                key: 'preferredLanguage',
                label: 'Langue préférée',
                render: (s: Singer) => s.profile?.preferredLanguage?.toUpperCase() || '—',
              },
            ]),
      ],
    },
  ]

  return (
    <div className="space-y-8">
      {/* User Details Panel */}
      <UserDetailsPanel
        user={singer}
        loading={loading}
        error={error}
        sections={detailSections}
        userType="singer"
        onAction={handleAction}
        onBack={() => navigate('/singers')}
        showActions={true}
        isSuspended={singer?.isSuspended || false}
      />

      {/* Action Modal */}
      <UserActionModal
        isOpen={modalState.isOpen}
        action={modalState.action}
        userType="singer"
        userName={
          singer
            ? singer.isGuest
              ? singer.profile?.pseudo || 'Chanteur invité'
              : `${singer.profile?.firstName} ${singer.profile?.lastName}`
            : ''
        }
        isLoading={modalState.isLoading}
        onConfirm={handleConfirmAction}
        onCancel={() =>
          setModalState({
            isOpen: false,
            action: null,
            isLoading: false,
          })
        }
      />

      {/* Events List Section */}
      {singer && singer.events && singer.events.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white font-mono">
                  {'> '}
                  <span className="text-purple-400">events</span>
                  {' --participated'}
                </h2>
                <p className="text-slate-400 text-xs mt-1 font-mono">
                  Liste des événements auxquels le chanteur a participé ({singer.events.length})
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Chansons chantées
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Total chansons
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-purple-400 text-xs uppercase tracking-wider">
                    Total chanteurs
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {singer.events.map((event) => (
                  <tr
                    key={event.id}
                    onClick={() => navigate(`/events/${event.id}`)}
                    className="hover:bg-slate-800/50 transition group cursor-pointer"
                  >
                    <td className="px-6 py-4 text-slate-200 font-semibold">{event.name}</td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {new Date(event.date).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded text-xs font-bold ${
                          event.status === 'active'
                            ? 'bg-green-900/50 text-green-300 border border-green-700/50'
                            : event.status === 'completed'
                            ? 'bg-blue-900/50 text-blue-300 border border-blue-700/50'
                            : event.status === 'draft'
                            ? 'bg-slate-700/50 text-slate-300 border border-slate-600/50'
                            : 'bg-red-900/50 text-red-300 border border-red-700/50'
                        }`}
                      >
                        {event.status === 'active'
                          ? 'Actif'
                          : event.status === 'completed'
                          ? 'Terminé'
                          : event.status === 'draft'
                          ? 'Brouillon'
                          : 'Annulé'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faMicrophone} className="text-purple-400 text-sm" />
                        <span className="text-purple-300 font-bold">{event.songsSungByUser || 0}</span>
                        {singer?.isGuest && (
                          <span className="text-xs text-slate-400 italic">(ajoutées)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faMusic} className="text-slate-400 text-sm" />
                        <span className="text-slate-300">{event.songsCount || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faUsers} className="text-slate-400 text-sm" />
                        <span className="text-slate-300">{event.singersCount || 0}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

